import React from 'react'

/**
 * Helpers and types
 */
import {pure} from '@react/store'
import {http} from '@helpers/common'
import string from '@helpers/string'
import {datetime} from '@helpers/common'
import {DefaultComponentProps} from '@interface/common'


/**
 * Assets
 */
import './organizations.scss'
import Spinner from '@assets/images/spinner.gif'


/**
 * Components
 */
import Search from '@components/search'
import Checkbox from '@components/checkbox'
import TableBottom from '@components/table/bottom'



/**
 * Organizations Screen
 */
export default pure(({store, modal}: DefaultComponentProps) => {
  const state = store.useLocalState({
    name: null,
    email: null,
    limit: 5,
    next: null,
    status: null,
    current: 1,
    organizations: [],
    selected: [],
    fetching: true,
  })
  const org = store.get('organization')


  /**
   * Get organizations
   */
  React.useEffect(() => {
    getGroups({limit: 10})
  }, [])


  /**
   * Update the table
   */
  React.useEffect(() => {
    
    if(org) {
      state.set(({organizations}:any) => {
        return {
          organizations: [org].concat(organizations)
        }
      })
    }
  }, [store])


  /**
   * Get current page
   */
  function page(name: string, page: string|number, last?: string|number) {
    if(page) {
      getGroups({last, [name]: page})
    }
  }


  
  /**
   * Is checked?
   */
  function isChecked() {
    if(state.selected.length === 0) {
      return false
    }
    return state.selected.length === state.organizations.length ? true : false
  }


  /**
   * Get organizations
   */
  function getGroups(query:any = {}) {
    http.get('/organizations/groups', {
      query: {
        limit: state.limit,
        status: state.status,
        ...query,
      }
    })
    .then(({status, result}) => {
      if(status === 200) {
        state.set({
          fetching: false,
          total: result.total,
          groups: result.groups ?? [],
        })
      }
    })
    .catch(e => console.log(e))
  }


  return (
    <React.Fragment>
      <header>
        <div className="left inline">
          <h3 className="margin-0">Groups</h3>
        </div>
        <div className="right inline">
          <span className="search">
            <label>Search</label>
            <Search
              type={'organization'}
              onChange={(data: object[]) => {
                state.set({
                  organizations: data
                })
              }}
            />
          </span>
          <div className="filter">
            <label>Status</label>
            <select
              onChange={(e) => {
                getGroups({status: e.target.value})
              }}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="invited">Invited</option>
            </select>
          </div>
          <div className="add-button">
            <span
              className="button-3"
              onClick={() => {
                modal.open('addOrg')
              }}>
              <span className="icon-plus"></span>
              <span className="text">Add Organization</span>
            </span>
          </div>
        </div>
      </header>
      <div className="table">
        <table>
          <thead>
            <tr>
              <th>
                <Checkbox
                  space={30}
                  onClick={(checked: boolean) => {
                    state.set({
                      selected: state.groups.reduce(
                        (items: any[], item: any) => {
                          if(checked) {
                            items.push({
                              oid: item.oid,
                              name: item.name,
                              email: item.email
                            })
                          }
                          else {
                            return items.filter((v: any) => v.oid !== item.oid)
                          }
                          return items
                        },
                      [])
                    })
                  }}
                  checked={isChecked()}
                />
              </th>
              <th>Name</th>
              <th>Space</th>
              <th>Organization</th>
              <th>Members</th>
              <th>Admins</th>
              <th>Type</th>
              <th>Status</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {state.fetching ? (
              <tr>
                <td colSpan={9} style={{textAlign: 'center'}}>
                  <img alt={'Spinner'} src={Spinner} style={{width: 32, padding: 20}}/>
                </td>
              </tr>
            ):(
              <>
                {state.groups.length > 0 ? (
                  <>
                    {state.groups.map((item: any, key: number) => {
                      const date = datetime(item.created)
                      const creator = item.admins.find((v:any) => v.uid === item.creator)
                      return (
                        <tr key={key}>
                          <td style={{width: 20}}>
                            <Checkbox
                              space={30}
                              onClick={(checked: boolean) => {
                                state.set(({selected}: any) => {
                                  if(checked) {
                                    selected.push(
                                      {oid: item.oid, name: item.name, email: item.email}
                                    )
                                  }
                                  else {
                                    selected = selected.filter((v: any) => v.oid !== item.oid)
                                  }
                                  return {selected}
                                })
                                
                              }}
                              checked={(
                                state.selected.filter((v: any) => v.oid === item.oid).length > 0
                              )}
                            />  
                          </td>
                          <td>{item.name}</td>
                          <td>{item.space}</td>
                          <td>{creator.organization?.name}</td>
                          <td>{item.members}</td>
                          <td>{item.admins.length}</td>
                          <td>{item.type}</td>
                          <td>
                            {string.toUCFirst(item.status)}
                          </td>

                          <td style={{width: 250}}>{date.getDate()}</td>
                          <td style={{width: 100}}>
                            <div className="flex-end">
                              <span
                                className="button-1"
                                onClick={() => {
                                  modal.open({name: 'groupControls', data: item})
                                }}>
                                <span>Manage</span>
                              </span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </>
                ):(
                  <tr>
                    <td colSpan={6} style={{textAlign: 'center'}}>
                      <span>No Results</span>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      <TableBottom
        entries={{
          onChange(e) {
            getGroups({limit: e.target.value})
          }
        }}
        pagination={{
          limit: state.limit,
          total: state.total,
          onClick(selected) {
            page('page', selected, state.organizations.at(-1)?.created)
          },
          onPrev() {
            page('prev', state.organizations[0]?.created)
          },
          onNext() {
            page('next', state.organizations.at(-1)?.created)
          },
        }}
      />
    </React.Fragment>
  )
})